import type { DeviceCapability, PageEffectsConfig, PerformanceMode } from '@/types/visualEffects';

function getMemoryScore(): number {
  const nav = navigator as Navigator & { deviceMemory?: number };
  if (nav.deviceMemory === undefined) return 0;
  if (nav.deviceMemory >= 8) return 3;
  if (nav.deviceMemory >= 4) return 1;
  return -2;
}

function getCpuScore(): number {
  if (navigator.hardwareConcurrency === undefined) return 0;
  if (navigator.hardwareConcurrency >= 8) return 2;
  if (navigator.hardwareConcurrency >= 4) return 1;
  return -1;
}

function getConnectionScore(): number {
  const connection = (
    navigator as Navigator & { connection?: { effectiveType?: string; saveData?: boolean } }
  ).connection;
  if (!connection) return 0;
  let score = 0;
  if (connection.saveData) score -= 3;
  if (connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g') {
    score -= 2;
  }
  return score;
}

function getTouchScore(): number {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0 ? -1 : 0;
}

function getDpiScore(): number {
  return window.devicePixelRatio > 2 ? -1 : 0;
}

function scoreToCapability(score: number): DeviceCapability {
  if (score >= 7) return 'high';
  if (score >= 4) return 'medium';
  return 'low';
}

export function detectDeviceCapability(): DeviceCapability {
  if (shouldReduceMotion()) {
    return 'low';
  }

  const score =
    5 + getMemoryScore() + getCpuScore() + getConnectionScore() + getTouchScore() + getDpiScore();

  return scoreToCapability(score);
}

/**
 * Check if user prefers reduced motion.
 */
export function shouldReduceMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Get effective performance level based on mode and detection.
 */
export function getEffectivePerformance(
  mode: PerformanceMode,
  detected: DeviceCapability
): DeviceCapability {
  switch (mode) {
    case 'high':
      return 'high';
    case 'low':
      return 'low';
    case 'auto':
    default:
      return detected;
  }
}

/**
 * Optimize page config based on device capability.
 */
export function getOptimalConfig(
  config: PageEffectsConfig,
  capability: DeviceCapability
): PageEffectsConfig {
  if (capability === 'high') {
    // Full quality
    return config;
  }

  // Create optimized copy
  const optimized = JSON.parse(JSON.stringify(config)) as PageEffectsConfig;

  if (capability === 'medium') {
    // Reduce particle count
    optimized.particles.count = Math.min(optimized.particles.count, 50);

    // Disable sparkle and drift (less CPU intensive)
    optimized.particles.sparkleEnabled = false;
    optimized.particles.driftEnabled = false;

    // Reduce blur
    if (optimized.blur.amount > 5) {
      optimized.blur.amount = 5;
    }
  } else {
    // Low capability - minimal effects

    // Significantly reduce particles
    optimized.particles.count = Math.min(optimized.particles.count, 15);
    optimized.particles.interactive = false;

    // Disable expensive effects
    optimized.particles.sparkleEnabled = false;
    optimized.particles.driftEnabled = false;
    optimized.particles.glowEnabled = false;
    optimized.particles.depthEnabled = false;

    // Disable blur (expensive)
    optimized.blur.enabled = false;

    // Simplify gradients
    optimized.gradient.size = Math.min(optimized.gradient.size, 1);
  }

  return optimized;
}

/**
 * Check if device supports OffscreenCanvas.
 */
export function supportsOffscreenCanvas(): boolean {
  return typeof OffscreenCanvas !== 'undefined';
}

/**
 * Check if device supports canvas filter.
 */
export function supportsCanvasFilter(): boolean {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;

  // Try to set a filter
  try {
    ctx.filter = 'blur(1px)';
    return ctx.filter === 'blur(1px)';
  } catch {
    return false;
  }
}

/**
 * Get recommended FPS based on capability.
 */
export function getRecommendedFPS(capability: DeviceCapability): number {
  switch (capability) {
    case 'high':
      return 60;
    case 'medium':
      return 30;
    case 'low':
      return 20;
  }
}
